/**
 * Retourne une couleur hexadécimale aléatoire
 * @returns {string} - La couleur hexadécimale aléatoire
 */
export const getRandomColor = () => {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

/**
 * Génère un alias basé sur l'heure de début et l'heure de fin
 * @param {number} heureDebut - Heure de début en secondes
 * @param {number} heureFin - Heure de fin en secondes
 * @returns {string} - Alias généré au format "HH:MM - HH:MM"
 */
export const generateAlias = (heureDebut, heureFin) => {
  const pad = (num) => num.toString().padStart(2, "0");
  const startHours = Math.floor(heureDebut / 3600);
  const startMinutes = (heureDebut % 3600) / 60;
  const endHours = Math.floor(heureFin / 3600);
  const endMinutes = (heureFin % 3600) / 60;
  return `${pad(startHours)}:${pad(startMinutes)} - ${pad(endHours)}:${pad(
    endMinutes
  )}`;
};

/**
 * Regroupe les tranches par date
 * @param {Array} tranches - La liste des tranches
 * @returns {Array} - La liste des tranches groupées par date
 */
export const groupTranchesByDate = (tranches) => {
  if (!tranches) return [];

  // Trier les tranches par DateActivite et HeureDebut
  const sortedTranches = tranches.sort((a, b) => {
    const dateA = new Date(a.DateActivite);
    const dateB = new Date(b.DateActivite);
    if (dateA < dateB) return -1;
    if (dateA > dateB) return 1;
    return a.HeureDebut - b.HeureDebut;
  });

  // Grouper les tranches par longDate
  const grouped = sortedTranches.reduce((acc, tranche) => {
    const date = tranche.longDate?.replace(/,\s/, " ");
    if (!date) return acc; // Skip tranches without a longDate
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(tranche);
    return acc;
  }, {});

  return Object.entries(grouped);
};

/**
 * Extrait les noms de parents uniques des utilisateurs
 * @param {Array} users - La liste des utilisateurs
 * @returns {Array} - La liste des noms de parents uniques
 */
export const extractUniqueParentNoms = (users) => {
  const uniqueParentNoms = {};

  users.forEach((user) => {
    user.wish?.tranches?.forEach((tranche) => {
      const key = `${tranche.parentId}-${tranche.parentNom}`;
      if (!uniqueParentNoms[key]) {
        uniqueParentNoms[key] = {
          nom: tranche.parentNom,
          id: tranche.parentId,
        };
      }
    });
  });

  return Object.values(uniqueParentNoms) || [];
};

/**
 * Extrait les dates et heures uniques des utilisateurs
 * @param {Array} users - La liste des utilisateurs
 * @returns {Object} - Les dates et heures uniques triées
 */
export const extractUniqueDatesAndTimes = (users) => {
  const datesDebut = new Set();
  const datesFin = new Set();
  const timesDebut = new Set();
  const timesFin = new Set();

  users?.forEach((user) => {
    user.wish?.tranches?.forEach((tranche) => {
      datesDebut.add(tranche.shortDate);
      timesDebut.add(Number(tranche.HeureDebut));
      datesFin.add(tranche.shortDate);
      timesFin.add(Number(tranche.HeureFin));
    });
  });

  // Convertir les ensembles en tableaux et les trier
  const sortedDatesDebut = Array.from(datesDebut).sort(
    (a, b) => new Date(a) - new Date(b)
  );
  const sortedTimesDebut = Array.from(timesDebut).sort((a, b) => a - b);
  const sortedDatesFin = Array.from(datesFin).sort(
    (a, b) => new Date(a) - new Date(b)
  );
  const sortedTimesFin = Array.from(timesFin).sort((a, b) => a - b);

  return {
    datesD: sortedDatesDebut || [],
    timesD: sortedTimesDebut || [],
    datesF: sortedDatesFin || [],
    timesF: sortedTimesFin || [],
  };
};


export const parseDateString = (dateString) => {
  const [day, month, year] = dateString.split(".");
  return new Date(`${year}-${month}-${day}T00:00:00Z`);
};

// Fonction pour convertir les secondes en format HH:MM
export const secondsToHHMM = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

// Fonction pour vérifier si une tranche est entièrement dans la plage de dates et heures spécifiée
export const isInDateAndTimeRange = (tranche, startDate, endDate, startTime, endTime) => {
  if (!startDate && !endDate && !startTime && !endTime) return true;

  // Créer des objets Date pour les dates
  const trancheStartDate = new Date(tranche.DateActivite);
  const trancheEndDate = new Date(tranche.DateActivite);

  // Vérifier si les dates sont valides
  if (isNaN(trancheStartDate.getTime()) || isNaN(trancheEndDate.getTime())) {
    console.error('Invalid Date in tranche:', tranche.DateActivite);
    return false;
  }

  // Construire les objets Date pour les heures de tranche
  const trancheStartTime = tranche.HeureDebut ? parseInt(tranche.HeureDebut, 10) : 0;
  const trancheEndTime = tranche.HeureFin ? parseInt(tranche.HeureFin, 10) : 86400; // 86400 secondes = 24 heures

  // Convertir heures en format HH:MM
  // const trancheStartTimeFormatted = secondsToHHMM(trancheStartTime);
  // const trancheEndTimeFormatted = secondsToHHMM(trancheEndTime);

  // Construire les objets Date pour les heures spécifiées
  const parsedStartTime = startTime ? parseInt(startTime, 10) : 0;
  const parsedEndTime = endTime ? parseInt(endTime, 10) : 86400; // 86400 secondes = 24 heures

  // Comparer les heures en secondes
  const isTimeAfterOrEqualStart = !startTime || trancheStartTime >= parsedStartTime;
  const isTimeBeforeOrEqualEnd = !endTime || trancheEndTime <= parsedEndTime;

  // Comparer les heures avec les dates spécifiées
  const isDateAfterOrEqualStart = !startDate || trancheStartDate >= new Date(parseDateString(startDate));
  const isDateBeforeOrEqualEnd = !endDate || trancheEndDate <= new Date(parseDateString(endDate));
  const isWithinDateRange = !startDate || !endDate ||
    (trancheStartDate >= new Date(parseDateString(startDate)) &&
      trancheEndDate <= new Date(parseDateString(endDate)));

  return (startDate && endDate && isWithinDateRange) ||
    (startDate && !endDate && isDateAfterOrEqualStart && isTimeAfterOrEqualStart) ||
    (!startDate && endDate && isDateBeforeOrEqualEnd && isTimeBeforeOrEqualEnd) ||
    (!startDate && !endDate && startTime && endTime && isTimeAfterOrEqualStart && isTimeBeforeOrEqualEnd) ||
    (!startDate && !endDate && startTime && !endTime && isTimeAfterOrEqualStart) ||
    (!startDate && !endDate && !startTime && endTime && isTimeBeforeOrEqualEnd);
};

/**
 * Regroupe les tranches par date pour le modal
 * @param {Array} tranches - La liste des tranches
 * @returns {Object} - Les tranches groupées par date
 */
export const groupTranchesByDateModal = (tranches) => {
  if (!tranches) {
    return {};
  }
  return tranches.reduce((acc, tranche) => {
    const date = tranche.DateActivite || "Date non définie"; // Fournir une valeur par défaut si DateActivite est manquant
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(tranche);
    return acc;
  }, {});
};

/**
 * Compte le nombre de correspondances d'activités par ID et calcule la durée totale
 * @param {Array} historique - L'historique des activités
 * @param {number} idActiviter - L'ID de l'activité
 * @returns {Object} - Le nombre de correspondances et la durée totale au format "MM:SS"
 */
export const countIdActiviteMatches = (historique, idActiviter) => {
  if (!historique || !Array.isArray(historique)) {
    return { count: 0, totalDuration: "00:00" };
  }

  let totalCount = 0;
  let totalMinutes = 0;
  let totalSeconds = 0;

  historique.forEach((item) => {
    if (item.Activite && Array.isArray(item.Activite)) {
      item.Activite.forEach((activite) => {
        if (activite.idAct === idActiviter) {
          totalCount += 1;

          if (activite.duree) {
            const [minutes, seconds] = activite?.duree
              ? activite.duree.split(":").map(Number)
              : [0, 0];
            totalMinutes += minutes;
            totalSeconds += seconds;
          }
        }
      });
    }
  });

  // Convertir le total des minutes et secondes au format MM:SS
  totalMinutes += Math.floor(totalSeconds / 60);
  totalSeconds = totalSeconds % 60;

  const totalDuration = `${String(totalMinutes).padStart(2, "0")}:${String(
    totalSeconds
  ).padStart(2, "0")}`;

  return { count: totalCount, totalDuration };
};

/**
 * Filtre les utilisateurs selon les règles spécifiées
 * @param {Array} users - La liste des utilisateurs
 * @param {Object} activeStep - L'étape active avec le type
 * @param {Object} selectedParent - La catégorie sélectionnée
 * @returns {Array} - La liste des utilisateurs filtrés
 */
export const filterUsers = (users, activeStep = {}, selectedParent) => {
  if (users.length === 0) {
    return [];
  }
  let filteredUsers = users;

  if (activeStep?.type !== "listeAttente") {
    // Si ce n'est pas une liste d'attente, on filtre par sous-activité
    filteredUsers = users.filter((user) => {
      return user.wish?.tranches?.some((tranche) => tranche.parentId === selectedParent?.id);
    });
  }

  // Filtrer par statut
  if (activeStep?.type === "wish") {
    filteredUsers = filteredUsers.filter((user) => {
      let isFirstCatNotTreated = false;
      user.wish?.ChoixActivites?.every((choix) => {
        if (!choix.treated) {
          if (selectedParent?.id === choix.id || selectedParent?.ID === choix.id) {
            isFirstCatNotTreated = true;
          }
          return false;
        }
        return true;
      });
      return user.statut === activeStep.type && isFirstCatNotTreated;
    });
  } else if (activeStep?.type === "preValider" || activeStep?.type === "valide") {
    filteredUsers = filteredUsers.filter((user) => {
      return user.statut === activeStep?.type && user.tempo?.some((choix) => choix.parentId === selectedParent?.id);
    });
  } else if (activeStep?.type === "listeAttente") {
    filteredUsers = filteredUsers.filter((user) => {
      return !user.wish?.ChoixActivites?.some((choix) => choix.treated === false);
    });
  } else {
    filteredUsers = filteredUsers.filter((user) => user.statut === activeStep?.type);
  }

  return filteredUsers;
};


/**
 * Ajoute une date formatée longue à une tranche si elle n'est pas déjà présente
 * @param {Object} tranche - La tranche horaire à laquelle ajouter la date formatée
 * @returns {Object} - La tranche horaire avec la date formatée ajoutée
 */
export const addLongDate = (tranche) => {
  // Vérifie si la tranche n'a pas déjà une date formatée longue
  if (!tranche.longDate) {
    // Crée un objet Date à partir de la date de l'activité de la tranche
    const date = new Date(tranche.DateActivite);
    // Ajoute la date formatée longue à la tranche
    tranche.longDate = date.toLocaleDateString("fr-FR", {
      weekday: "short", // Affiche le jour de la semaine abrégé
      day: "numeric", // Affiche le jour du mois
      month: "short", // Affiche le mois abrégé
      year: "numeric", // Affiche l'année
    }); // Assure l'ajout d'un espace après la virgule
  }
  // Retourne la tranche avec la date formatée ajoutée
  return tranche;
};

/**
 * Supprime la virgule en tête d'une chaîne de caractères si elle est présente
 * @param {string} str - La chaîne de caractères à traiter
 * @returns {string} - La chaîne de caractères sans la virgule en tête
 */
export const removeLeadingComma = (str) => {
  // Vérifie si la chaîne commence par une virgule
  if (str.startsWith(",")) {
    // Supprime la virgule en tête et retourne la chaîne modifiée
    return str.slice(1);
  }
  // Retourne la chaîne originale si elle ne commence pas par une virgule
  return str;
};

/**
 * Calcule le nombre total d'heures pour une activité spécifique d'un utilisateur.
 *
 * Cette fonction parcourt l'historique de l'utilisateur et additionne le temps total
 * passé sur toutes les tranches d'une activité spécifiée par `parentId`.
 *
 * @param {Object} user - L'utilisateur dont on veut calculer les heures
 * @param {number} parentId - L'identifiant de l'activité pour laquelle calculer les heures
 * @returns {number} - Le nombre total d'heures pour l'activité spécifiée
 */
export const calculateTotalHours = (user, parentId) => {
  // Vérifie que l'utilisateur a un historique et que cet historique contient des entrées
  if (!user.Historique || !user.Historique.Historique) {
    return 0; // Retourne 0 si l'historique est vide ou non défini
  }

  let totalHours = 0; // Initialise le compteur total d'heures

  // Parcourt chaque entrée de l'historique de l'utilisateur
  user.Historique.Historique.forEach((historique) => {
    // Parcourt chaque activité dans l'entrée historique
    historique.Activite.forEach((activite) => {
      // Vérifie si l'activité correspond à l'ID spécifié
      if (activite.idAct === parentId) {
        // Parcourt chaque tranche de temps de l'activité
        activite.Tranches.forEach((tranche) => {
          // Convertit l'heure de début et de fin en heures et minutes
          const [startHour, startMinute] = tranche.Debut.split(":").map(Number);
          const [endHour, endMinute] = tranche.Fin.split(":").map(Number);
          const startTime = new Date(0, 0, 0, startHour, startMinute); // Crée un objet Date pour l'heure de début
          const endTime = new Date(0, 0, 0, endHour, endMinute); // Crée un objet Date pour l'heure de fin
          // Calcule la différence en millisecondes, puis convertit en heures
          const diff = (endTime - startTime) / (1000 * 60 * 60); // Convertit la différence en heures
          totalHours += diff; // Ajoute la durée de la tranche au total
        });
      }
    });
  });

  return totalHours; // Retourne le total des heures pour l'activité spécifiée
};
